import { Controller } from '@hotwired/stimulus';

// Mute schedule form controller
// Handles start timing, end timing, field visibility, and timeline preview
export default class extends Controller {
  static targets = [
    'startInput',
    'startFields',
    'startDateInput',
    'startTimeInput',
    'endInput',
    'endFields',
    'endTimingField',
    'endDateField',
    'endTimeField',
    'endDateInput',
    'endTimeInput',
    'timeline',
    'manualTimeline',
    'resumeTime',
    'resumeTimeText',
    'progressBar',
  ];

  static values = {
    progressMap: Object, // e.g. { "60": 4, "240": 17, "1440": 50, "10080": 100 }
  };

  connect() {
    this.updateStart();
    this.updateEnd();
  }

  updateStart() {
    const selectedInput = this.startInputTargets.find((input) => input.checked);
    if (!selectedInput) return;

    const isScheduled = selectedInput.value === 'scheduled';

    // Show/hide start fields
    if (this.hasStartFieldsTarget) {
      this.startFieldsTargets.forEach((el) =>
        el.classList.toggle('d-none', !isScheduled)
      );
    }

    // Recalculate end time when start timing changes
    this.updateEnd();
  }

  getStartTime() {
    const selectedInput = this.startInputTargets.find((input) => input.checked);
    const isScheduled = selectedInput?.value === 'scheduled';

    if (
      isScheduled &&
      this.hasStartDateInputTarget &&
      this.hasStartTimeInputTarget
    ) {
      const dateValue = this.startDateInputTarget.value;
      const timeValue = this.startTimeInputTarget.value;

      if (dateValue && timeValue) {
        return new Date(`${dateValue}T${timeValue}`);
      }
    }

    return new Date();
  }

  updateEnd() {
    const selectedInput = this.endInputTargets.find((input) => input.checked);
    if (!selectedInput) return;

    const value = selectedInput.value;
    const isManual = value === 'manual';
    const isCustom = value === 'custom';
    const isDuration = !isManual && !isCustom;

    // Update hidden fields based on selection
    if (isManual) {
      this.endTimingFieldTarget.value = 'manual';
      // Disable all end date/time fields for manual
      this.endDateFieldTarget.disabled = true;
      this.endTimeFieldTarget.disabled = true;
      if (this.hasEndDateInputTarget) this.endDateInputTarget.disabled = true;
      if (this.hasEndTimeInputTarget) this.endTimeInputTarget.disabled = true;
    } else if (isCustom) {
      this.endTimingFieldTarget.value = 'specific_time';
      // Disable hidden fields, enable visible inputs
      this.endDateFieldTarget.disabled = true;
      this.endTimeFieldTarget.disabled = true;
      if (this.hasEndDateInputTarget) this.endDateInputTarget.disabled = false;
      if (this.hasEndTimeInputTarget) this.endTimeInputTarget.disabled = false;
    } else if (isDuration) {
      this.endTimingFieldTarget.value = 'specific_time';
      // Calculate end date/time from duration and set hidden fields
      // Use scheduled start time if set, otherwise use current time
      const minutes = parseInt(value, 10);
      const startTime = this.getStartTime();
      const endTime = new Date(startTime.getTime() + minutes * 60 * 1000);
      this.endDateFieldTarget.value = this.formatDate(endTime);
      this.endTimeFieldTarget.value = this.formatTime(endTime);
      this.endDateFieldTarget.disabled = false;
      this.endTimeFieldTarget.disabled = false;
      // Disable visible inputs so hidden ones are used
      if (this.hasEndDateInputTarget) this.endDateInputTarget.disabled = true;
      if (this.hasEndTimeInputTarget) this.endTimeInputTarget.disabled = true;
    }

    // Show/hide sections
    if (this.hasEndFieldsTarget) {
      this.endFieldsTargets.forEach((el) =>
        el.classList.toggle('d-none', !isCustom)
      );
    }
    if (this.hasTimelineTarget) {
      this.timelineTargets.forEach((el) =>
        el.classList.toggle('d-none', !isDuration)
      );
    }
    if (this.hasManualTimelineTarget) {
      this.manualTimelineTargets.forEach((el) =>
        el.classList.toggle('d-none', !isManual)
      );
    }

    // Update timeline for duration-based selections
    if (isDuration) {
      this.updateTimeline(parseInt(value, 10));
    }
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatTime(date) {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  updateTimeline(minutes) {
    const startTime = this.getStartTime();
    const resumeTime = new Date(startTime.getTime() + minutes * 60 * 1000);

    // Check if resume time is on a different day than today
    const today = new Date();
    const isNotToday = resumeTime.toDateString() !== today.toDateString();

    // Format time - include weekday/date for longer durations or future dates
    const shortFormat = { hour: 'numeric', minute: '2-digit', hour12: true };
    const mediumFormat = {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    const longFormat = {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };

    const isLong = minutes >= 1440 || isNotToday; // 1 day+ or different day
    const isMedium = minutes >= 60 || isNotToday; // 1 hour+ or different day

    const timeString = resumeTime.toLocaleString(
      [],
      isMedium ? mediumFormat : shortFormat
    );
    const fullTimeString = resumeTime.toLocaleString(
      [],
      isLong ? longFormat : mediumFormat
    );

    // Update displays
    this.resumeTimeTargets.forEach((el) => (el.textContent = timeString));
    this.resumeTimeTextTargets.forEach(
      (el) => (el.textContent = fullTimeString)
    );

    // Update progress bar
    const progressMap = this.hasProgressMapValue ? this.progressMapValue : {};
    const progress = progressMap[minutes.toString()] || 50;
    this.progressBarTargets.forEach((el) => (el.style.width = `${progress}%`));
  }
}
